<template>
  <mailbox-settings-layout :name="name">
    <el-card class="mailbox-settings-workflow-automations">
      <template #header>
        <h3>Workflow Automations</h3>
      </template>
      <entity-settings-loading v-if="isLoading" />
      <template v-else>
        <p>
          Choose what happens automatically when a conversation changes to each status:
          set a due date, and add checklists from your
          <router-link :to="RouteAssistant.getMailboxSettingsChecklistTemplateRoute($route.params.accountId, id)" class="just-a-link">checklist templates</router-link>.
        </p>
        <div v-for="status in listStatuses" :key="status.id" class="workflow-automation-status">
          <div class="workflow-automation-status__header">
            <status-chicklet :status="status" size="medium" />
            <span class="workflow-automation-status__summary">{{ automationSummary(status.id) }}</span>
          </div>
          <!-- Due date -->
          <div class="workflow-automation-status__section">
            <strong class="workflow-automation-status__section-title">Due date</strong>
            <div v-if="!isInactiveStatus(status.id)" class="workflow-automation-status__due-date">
              <el-switch
                v-model="dueDateSettings[status.id].useDueDateOffset"
                :disabled="isSubmitting"
                @change="saveDueDateSetting(status.id)"
              />
              <span v-if="!dueDateSettings[status.id].useDueDateOffset">Set a due date when a conversation changes to this status</span>
              <template v-else>
                <span>Due</span>
                <el-input
                  v-model.number="dueDateSettings[status.id].dueDateOffset"
                  :disabled="isSubmitting"
                  type="number"
                  min="1"
                  @change="saveDueDateSetting(status.id)"
                />
                <single-select
                  v-model="dueDateSettings[status.id].dueDateOffsetType"
                  :disabled="isSubmitting"
                  @change="saveDueDateSetting(status.id)"
                >
                  <el-option v-for="unit in dueDateUnits" :key="unit.id" :value="unit.id" :label="unit.name" />
                </single-select>
                <span>after the conversation changes to this status</span>
              </template>
            </div>
            <p v-else class="workflow-automation-status__hint">
              Inactive statuses can't set a due date.
            </p>
          </div>
          <!-- Checklists -->
          <div class="workflow-automation-status__section">
            <strong class="workflow-automation-status__section-title">Checklists</strong>
            <p class="workflow-automation-status__hint">
              When a conversation changes to this status, these checklists are added to it. A conversation never gets the same checklist twice.
            </p>
            <div
              v-for="template in templatesForStatus(status.id)"
              :key="template.id"
              class="workflow-automation-status__template"
            >
              <i class="ddi ddi-everything" />
              <span class="workflow-automation-status__template-title">{{ template.title }}</span>
              <span class="workflow-automation-status__template-count">{{ itemCountLabel(template) }}</span>
              <dd-icon-button
                :is-disabled="isSubmitting"
                icon="close"
                type="rounded"
                size="medium"
                color="grey"
                title="Stop adding this checklist on this status"
                @click="removeTemplateFromStatus(template)"
              />
            </div>
            <checklist-template-picker
              v-if="templatesNotOnStatus(status.id).length"
              :templates="templatesNotOnStatus(status.id)"
              :statuses="listStatuses"
              :is-disabled="isSubmitting"
              @template-selected="(templateID) => addTemplateToStatus(templateID, status.id)"
            >
              <dd-button :is-disabled="isSubmitting" type="text">+ Add a checklist</dd-button>
            </checklist-template-picker>
            <p v-else-if="!hasTemplates" class="workflow-automation-status__hint">
              This mailbox has no checklist templates yet.
              <router-link :to="RouteAssistant.getMailboxSettingsChecklistTemplateRoute($route.params.accountId, id)" class="just-a-link">Create one</router-link>
              to add it here.
            </p>
          </div>
        </div>
      </template>
    </el-card>
  </mailbox-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxSettingsLayout from '@/layouts/mailbox-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IChecklistTemplate, IItemStatus } from '@/api/types/response.types'
import { IWorkflowStatusProperty } from '@/api/types/request.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'
import WorkflowsAPI from '@/api/modules/workflows.api'

/* Mirrors Domain.Entities.Projections.Projects.DueDateOffsetTypes */
enum EDueDateOffsetTypes {
  NoDueDate = 0,
  Days = 1,
  Weeks = 2,
  Months = 3,
  Years = 4,
}

export default defineComponent({
  name: 'MailboxSettingsWorkflowAutomations',

  components: {
    MailboxSettingsLayout,
    EntitySettingsLoading,
  },

  data() {
    return {
      RouteAssistant,
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      name: '',
      listStatuses: [] as IItemStatus[],
      inactiveStatusIDs: [] as number[],
      checklistTemplates: [] as IChecklistTemplate[],
      dueDateSettings: {} as Record<number, IWorkflowStatusProperty>,
      dueDateUnits: [
        { id: EDueDateOffsetTypes.Days, name: 'Day(s)' },
        { id: EDueDateOffsetTypes.Weeks, name: 'Week(s)' },
        { id: EDueDateOffsetTypes.Months, name: 'Month(s)' },
        { id: EDueDateOffsetTypes.Years, name: 'Year(s)' },
      ],
    }
  },

  head(): any {
    return {
      title: `Workflow Automations - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    hasTemplates(): boolean {
      return this.checklistTemplates.length > 0
    },
  },

  async created() {
    this.isLoading = true
    await this.loadInitialData()
    this.isLoading = false
  },

  methods: {
    async loadInitialData() {
      try {
        const mailboxInfoResponse = await MailboxesAPI.get_WithWorkflow(this.id)
        this.name = mailboxInfoResponse.title

        const [workflowDetailResponse, statusProperties] = await Promise.all([
          WorkflowsAPI.get_Detail(mailboxInfoResponse.workflowID),
          MailboxesAPI.get_MailboxWorkflowStatusPropertiesGet(this.id),
          this.getTemplates(),
        ])
        const workflowDetail = workflowDetailResponse.detail
        this.listStatuses = workflowDetail.listStatuses
        this.inactiveStatusIDs = workflowDetail.listInactiveStatuses.map(
          s => s.id,
        )

        const dueDateSettings = {} as Record<number, IWorkflowStatusProperty>
        this.listStatuses.forEach(status => {
          const property = statusProperties.find(
            p => p.statusID === status.id,
          ) || {
            mailboxID: this.id,
            statusID: status.id,
            useDueDateOffset: false,
            dueDateOffsetType: EDueDateOffsetTypes.Days,
            dueDateOffset: 1,
          }
          if (property.dueDateOffsetType === EDueDateOffsetTypes.NoDueDate) {
            property.dueDateOffsetType = EDueDateOffsetTypes.Days
          }
          dueDateSettings[status.id] = property
        })
        this.dueDateSettings = dueDateSettings
      } catch (error) {
        this.$error.emit(error as any)
      }
    },

    async getTemplates() {
      this.checklistTemplates = await MailboxesAPI.get_ChecklistTemplates(
        this.id,
      )
    },

    templatesForStatus(statusID: number): IChecklistTemplate[] {
      return this.checklistTemplates.filter(t => t.statusID === statusID)
    },

    templatesNotOnStatus(statusID: number): IChecklistTemplate[] {
      return this.checklistTemplates.filter(t => t.statusID !== statusID)
    },

    itemCountLabel(template: IChecklistTemplate): string {
      const count = (template.checklistTemplateItems || []).length
      return count === 1 ? '1 item' : `${count} items`
    },

    automationSummary(statusID: number): string {
      const parts = []
      if (
        !this.isInactiveStatus(statusID) &&
        this.dueDateSettings[statusID].useDueDateOffset
      ) {
        parts.push('Sets a due date')
      }
      const checklistCount = this.templatesForStatus(statusID).length
      if (checklistCount) {
        parts.push(
          checklistCount === 1
            ? 'Adds 1 checklist'
            : `Adds ${checklistCount} checklists`,
        )
      }
      return parts.length ? parts.join(' · ') : 'No automations'
    },

    isInactiveStatus(statusID: number): boolean {
      return this.inactiveStatusIDs.includes(statusID)
    },

    async saveDueDateSetting(statusID: number) {
      const property = this.dueDateSettings[statusID]
      if (!property.dueDateOffset || property.dueDateOffset < 1) {
        property.dueDateOffset = 1
      }
      try {
        this.isSubmitting = true
        await MailboxesAPI.put_MailboxWorkflowStatusPropertiesUpdate(
          this.id,
          statusID,
          { property },
        )
      } catch (error) {
        this.$error.emit(error as any, true)
      } finally {
        this.isSubmitting = false
      }
    },

    async addTemplateToStatus(templateID: number, statusID: number) {
      const template = this.checklistTemplates.find(t => t.id === templateID)
      // A template belongs to one status, so picking it here takes it off its old status
      const previousStatus = this.listStatuses.find(
        s => s.id === template.statusID,
      )
      template.statusID = statusID
      const isSaved = await this.saveTemplate(template)
      if (isSaved && previousStatus) {
        this.$messaging.showSuccess(
          `Moved "${template.title}" here from ${previousStatus.name}.`,
        )
      }
    },

    async removeTemplateFromStatus(template: IChecklistTemplate) {
      template.statusID = null
      await this.saveTemplate(template)
    },

    async saveTemplate(template: IChecklistTemplate): Promise<boolean> {
      try {
        this.isSubmitting = true
        await MailboxesAPI.put_ChecklistTemplateUpdate(this.id, template.id, {
          checklistTemplate: template,
        })
        return true
      } catch (error) {
        this.$error.emit(error as any, true)
        return false
      } finally {
        await this.getTemplates()
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.mailbox-settings-workflow-automations {
  .workflow-automation-status {
    margin-top: 16px;
    border: $border--solid-light;
    border-radius: 8px;

    &__header {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      border-bottom: $border--solid-extra-light;
      border-radius: 8px 8px 0 0;
      background-color: var(--color-background-secondary);
    }

    &__summary {
      margin-left: auto;
      padding-left: 12px;
      @include lettering--p-sm;
      color: var(--color-text-third);
    }

    &__section {
      padding: 14px 16px;
      @include lettering--p;

      &:not(:last-child) {
        border-bottom: $border--solid-extra-light;
      }
    }

    &__section-title {
      display: block;
      margin-bottom: 6px;
    }

    &__hint {
      margin-bottom: 8px;
      @include lettering--p-sm;
      color: var(--color-text-third);

      &:last-child {
        margin-bottom: 0;
      }
    }

    &__due-date {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      gap: 8px;

      .el-switch {
        margin-right: 2px;
      }

      > .el-input {
        width: 90px;
      }

      > .el-select {
        width: 130px;
      }
    }

    &__template {
      display: flex;
      align-items: center;
      padding: 6px 0;

      > i {
        color: var(--color-text-third);
      }

      > :last-child {
        margin-left: auto;
      }
    }

    &__template-title {
      margin-left: 10px;
      @include lettering--p;
    }

    &__template-count {
      margin-left: 8px;
      @include lettering--p-sm;
      color: var(--color-text-third);
    }

    .checklist-template-picker {
      margin-top: 4px;
    }
  }
}
</style>
